/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { Library } from 'lucide-react';
import { type ChangeEvent, useCallback, useMemo, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { type GoalDomain, type TaxonomyGoal } from '../../lib/api/itp.js';

const DOMAINS: readonly GoalDomain[] = ['SLT', 'OT', 'ABA', 'PT', 'SPECED'];
const SELECT_CLASS =
  'focus-ring h-9 rounded-lg border border-slate-200 bg-white px-2 text-sm dark:border-slate-700 dark:bg-slate-900';

interface GoalTaxonomyPickerProps {
  readonly taxonomy: readonly TaxonomyGoal[];
  readonly onPick: (goal: TaxonomyGoal) => void;
}

/**
 * Accelerator for the ITP builder (BRD M01 #2): pick an evidence-based goal from
 * the shared library to prefill a goal row, instead of authoring from scratch.
 * Selecting a goal fires `onPick`; the row's fields stay fully editable after.
 */
export function GoalTaxonomyPicker({ taxonomy, onPick }: GoalTaxonomyPickerProps): JSX.Element {
  const { t } = useTranslation();
  const [domain, setDomain] = useState<GoalDomain | ''>('');

  const options = useMemo(
    () => (domain === '' ? taxonomy : taxonomy.filter((g) => g.domain === domain)),
    [taxonomy, domain],
  );

  const onDomain = useCallback((event: ChangeEvent<HTMLSelectElement>) => {
    setDomain(event.target.value as GoalDomain | '');
  }, []);
  const onGoal = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      const picked = taxonomy.find((g) => g.id === event.target.value);
      if (picked !== undefined) {
        onPick(picked);
      }
    },
    [taxonomy, onPick],
  );

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg bg-slate-50 p-2 dark:bg-slate-800/40">
      <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-500">
        <Library className="h-3.5 w-3.5" aria-hidden="true" />
        {t('therapist.planForm.fromLibrary')}
      </span>
      <select
        value={domain}
        onChange={onDomain}
        aria-label={t('therapist.planForm.pickDomain')}
        className={SELECT_CLASS}
      >
        <option value="">{t('therapist.planForm.allDomains')}</option>
        {DOMAINS.map((d) => (
          <option key={d} value={d}>
            {t(`therapist.planForm.domains.${d}`)}
          </option>
        ))}
      </select>
      <select
        value=""
        onChange={onGoal}
        aria-label={t('therapist.planForm.pickGoal')}
        className={`${SELECT_CLASS} min-w-[12rem] flex-1`}
      >
        <option value="">{t('therapist.planForm.pickGoal')}</option>
        {options.map((g) => (
          <option key={g.id} value={g.id}>
            {g.code} — {g.title}
          </option>
        ))}
      </select>
    </div>
  );
}
